import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledMuiAutoCompleteSelect, ControlledTextField, CustomTooltip, IOptionType } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import BarcodeRangeModelHelper from '../../../../@types/model/masterData/barcodeRange/barcodeRangeModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { IBarcodeRangeFormValues } from '../../../../@types/model/masterData/barcodeRange/barcodeRangeFormValues';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import OptionType from '../../../../@types/model/optionType';

interface IBarcodeRangeFormProps {
    isLoading : boolean;
    initialValues : IBarcodeRangeFormValues;
    onSubmit : (values : IBarcodeRangeFormValues) => void;
    onClose : () => void;
}

const BarcodeRangeForm = (props : IBarcodeRangeFormProps) : React.ReactElement => {
    const sites = useAppSelector(x => x.masterData.sites);
    const schema = BarcodeRangeModelHelper.formSchema();

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger, setValue, watch } = useForm<IBarcodeRangeFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const isExport = watch('isExport');

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);
    
    useEffect(() => {
        if (!isExport) {
            setValue('indicatorDigit', null, { shouldDirty: true, shouldValidate: true });
        }
        trigger();
    }, [isExport]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onRangeEndChange = () => {
        trigger();
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const siteOptions = useMemo<Array<IOptionType>>(() => {
        return sites
            .filter(x => x.isActive && x.isPackhouse)
            .map(OptionType.fromSite);
    }, [sites]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p30'}>
                    {isExport &&
                        <img
                            className={'mb20 wfill'}
                            height={150}
                            src={`${ASSET_BASE}/assets/images/gs1.png`}
                            alt={'GS1 Logo'} />
                    }
                    <ControlledTextField
                        className={'mb15'}
                        control={control}
                        label={'Name'}
                        name={'name'}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <CustomTooltip
                        title={!isExport ? 'Only for export ranges' : ''}
                        className={'wfill'}
                    >
                        <ControlledTextField
                            label={'Indicator Digit'}
                            name={'indicatorDigit'}
                            control={control}
                            className={'mb15 wfill'}
                            defaultValue={props.initialValues.indicatorDigit?.toString()}
                            disabled={!isExport}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <ControlledTextField
                        className={'mb15'}
                        control={control}
                        label={'Range Start'}
                        type={'number'}
                        name={'rangeStart'}
                        defaultValue={props.initialValues.rangeStart}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb15'}
                        control={control}
                        type={'number'}
                        label={'Range End'}
                        name={'rangeEnd'}
                        defaultValue={props.initialValues.rangeEnd}
                        size={'small'}
                        onChange={onRangeEndChange}
                    />
                    <CustomTooltip
                        title={!isExport ? 'Only for export ranges' : ''}
                        className={'wfill flx1'}
                    >
                        <ControlledTextField
                            className={'mb15 wfill'}
                            control={control}
                            label={'Import Code'}
                            name={'importCode'}
                            defaultValue={props.initialValues.importCode}
                            size={'small'}
                            disabled={!isExport}
                        />
                    </CustomTooltip>
                    <ControlledMuiAutoCompleteSelect
                        className={'mb15'}
                        control={control}
                        label={'Sites'}
                        isMulti
                        defaultValues={props.initialValues.sites}
                        options={siteOptions}
                        name={'sites'}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <div className={'fdr mr10'}>
                            <ControlledLabelledCheckBox
                                label={'Is Export?'}
                                name={'isExport'}
                                control={control}
                                defaultValue={props.initialValues.isExport}
                            />
                        </div>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default BarcodeRangeForm;
